import React, { memo, useEffect } from 'react';
import { Select } from '@mafc/trolly';
import { Trash, MinusBold, Plus } from '@mafc/icon';

import { isNonEmptyArray } from 'utils/helpers.util';
import IQuantityUpdateProps from '../../../Cart/components/UpdateQuantity/UpdateQuantity.interface';
import useQuantityUpdate from '../../../Cart/components/UpdateQuantity/hooks/useQuantityUpdate.hook';
import getDecreaseButtonProps from '../../../Cart/components/UpdateQuantity/helpers/getDecreaseButtonProps.helper';
import {
  hasReachedQtyMaxLimit,
  isQtyOnlyOne,
} from '../../../Cart/components/UpdateQuantity/helpers/getQuantityLimitStatus.helper';
import COLORS from 'theme/colors';

import * as S from './QuantityUpdate.style';

const QuantityUpdate: React.FC<IQuantityUpdateProps> = ({
  updateable,
  quantityList,
  curreny,
  onSubstituteQtyChange,
  ...restProps
}) => {
  const [
    qty,
    selectedValue,
    { onIncrementHandler, onDecrementHandler, onSelectHandler, onDeleteHandler },
  ] = useQuantityUpdate(restProps, quantityList);

  useEffect(() => {
    if (onSubstituteQtyChange) {
      onSubstituteQtyChange(qty);
    }
  }, [qty, onSubstituteQtyChange]);

  if (!updateable) {
    return null;
  }

  const { min, max } = restProps;

  const { isDelete, ...restDecreaseProps } = getDecreaseButtonProps({
    qty,
    min,
    onDecrementHandler,
    onDeleteHandler,
  });

  const isQtyNonUpdatable = isQtyOnlyOne(min, max);
  const showDelete = restProps.isSubstitute ? false : isDelete;
  const Icon = showDelete ? Trash : MinusBold;

  return (
    <>
      <S.QuantityUpdate>
        <S.ButtonWrapper variant={showDelete ? 'trash' : 'minus'}>
          <S.PlusMinusButton
            {...restDecreaseProps}
            testid="subtract"
            size="36px"
            variant="outlined"
            disabled={restProps.isSubstitute ? isDelete : false}
          >
            <Icon testid={showDelete ? 'Trash' : 'MinusBold'} size="XS" fill={COLORS.BLUE} />
          </S.PlusMinusButton>
        </S.ButtonWrapper>

        {isNonEmptyArray(quantityList) && (
          <S.SelectBox>
            <Select
              disabled={isQtyNonUpdatable}
              options={quantityList}
              value={selectedValue}
              onChange={onSelectHandler}
              testid="quantity"
              data-testid="__cart_list_item_selected_quantity__"
              m={0}
              variant="secondary"
              arrowStrokeColor={COLORS.BLACK}
              arrowStrokeWidth={'3'}
              background={COLORS.WHITE}
            />
          </S.SelectBox>
        )}

        <S.PlusMinusButton
          disabled={hasReachedQtyMaxLimit(qty, max) || isQtyNonUpdatable}
          onClick={onIncrementHandler}
          size="36px"
          testid="add"
          variant="outlined"
        >
          <Plus testid="Plus" size="XS" stroke={COLORS.BLUE} />
        </S.PlusMinusButton>
      </S.QuantityUpdate>
    </>
  );
};

export default memo(QuantityUpdate);
